/**
 * GL (chart-of-accounts) API
 * All GL-related API operations
 */

import { getStoredToken } from "../../../../services/api";
import { API_BASE_URL } from "../../../../config/api";
import type {
  GlsListResponseDTO,
  CreateGlPayloadDTO,
  UpdateGlPayloadDTO,
  GlCreateUpdateResponseDTO,
  GlDeleteResponseDTO,
  AccountTypeGroupsResponseDTO,
} from "../models/GlDTOs";

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  ACCEPTED: 202,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
};

async function getToken(): Promise<string> {
  const token = getStoredToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }
  return token;
}

/**
 * Fetch GLs with pagination and optional search
 * GET /api/gl-maps/list
 */
export async function fetchGls(params: {
  limit: number;
  offset: number;
  search?: string;
  account_type?: string;
  /** Pass true only for the management list views (GlsSection, AdminDashboard GL tab)
   *  so under-review GLs are included. All other callers (dropdowns) omit this. */
  management?: boolean;
}): Promise<GlsListResponseDTO> {
  const token = await getToken();
  const query = new URLSearchParams();
  query.set("limit", String(params.limit));
  query.set("offset", String(params.offset));
  if (params.search) query.set("search", params.search);
  if (params.account_type) query.set("account_type", params.account_type);
  if (params.management) query.set("management", "1");

  const response = await fetch(`${API_BASE_URL}/api/gl-maps/list?${query.toString()}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to fetch GLs: ${response.statusText}`,
      response.status,
      data
    );
  }
  return data as GlsListResponseDTO;
}

/**
 * Create GL
 * POST /api/gl-maps/create
 */
export async function createGl(payload: CreateGlPayloadDTO): Promise<GlCreateUpdateResponseDTO> {
  const token = await getToken();
  const response = await fetch(`${API_BASE_URL}/api/gl-maps/create`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok && response.status !== HTTP_STATUS.ACCEPTED) {
    throw new APIError(
      data.message || "Failed to create GL",
      response.status,
      data
    );
  }
  return data as GlCreateUpdateResponseDTO;
}

/**
 * Update GL
 * POST /api/gl-maps/update/:accountId
 */
export async function updateGl(
  accountId: string,
  payload: UpdateGlPayloadDTO
): Promise<GlCreateUpdateResponseDTO> {
  const token = await getToken();
  const response = await fetch(
    `${API_BASE_URL}/api/gl-maps/update/${encodeURIComponent(accountId)}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }
  );

  const data = await response.json().catch(() => ({}));
  if (!response.ok && response.status !== HTTP_STATUS.ACCEPTED) {
    throw new APIError(
      data.message || "Failed to update GL",
      response.status,
      data
    );
  }
  return data as GlCreateUpdateResponseDTO;
}

/**
 * Delete GL
 * POST /api/gl-maps/delete/:accountId
 */
export async function deleteGl(
  accountId: string,
  payload?: { request_comments?: string }
): Promise<GlDeleteResponseDTO> {
  const token = await getToken();
  const response = await fetch(
    `${API_BASE_URL}/api/gl-maps/delete/${encodeURIComponent(accountId)}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload ?? {}),
    }
  );

  const data = await response.json().catch(() => ({}));
  if (!response.ok && response.status !== HTTP_STATUS.ACCEPTED) {
    throw new APIError(
      data.message || "Failed to delete GL",
      response.status,
      data
    );
  }
  return data as GlDeleteResponseDTO;
}

/**
 * Check whether a GL account can be deleted (no associated transactions in Zoho).
 * GET /api/gl-maps/can-delete/:accountId
 */
export async function checkGlCanDelete(
  accountId: string
): Promise<{ can_delete: boolean; message?: string }> {
  const token = await getToken();
  const response = await fetch(
    `${API_BASE_URL}/api/gl-maps/can-delete/${encodeURIComponent(accountId)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || "Failed to check if GL can be deleted",
      response.status,
      data
    );
  }
  // Backend wraps response in { success, message, data: { can_delete } }
  return {
    can_delete: Boolean(data?.data?.can_delete),
    message: data?.data?.message ?? data?.message,
  };
}

/**
 * Get account type groups for dropdown
 * GET /api/gl-maps/account-type-groups
 */
export async function getAccountTypeGroups(): Promise<AccountTypeGroupsResponseDTO> {
  const token = await getToken();
  const response = await fetch(`${API_BASE_URL}/api/gl-maps/account-type-groups`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || "Failed to fetch account type groups",
      response.status,
      data
    );
  }
  return data as AccountTypeGroupsResponseDTO;
}
